/**
 * RichBlocks — the LEAF block renderers for the Copilot View Protocol.
 *
 * Every renderer here is defensive (missing/empty data degrades to `null` or a
 * muted placeholder, never a throw) and reuses the app's existing display kit
 * instead of inventing new UI:
 *   - `@/ui` (Card / CardGrid / CardThumb / DataGrid / InsightBadge)
 *   - `@/routes/analytics/charts` (KpiTile, DeltaBadge, BarChart, LineChart,
 *     DonutChart, Gauge, Heatmap, Sparkline, compactNumber)
 *   - `@/routes/crm/charts-extra` (PieChart, FunnelChart, Leaderboard)
 *
 * The three CONTAINER blocks (`dashboard` / `modal` / `tabs`) live in
 * `registry.tsx` instead of here, because they recurse back into the block
 * dispatcher — keeping them there avoids a circular import between this file
 * and the registry.
 *
 * Markdown is rendered by a tiny hand-rolled parser (headings / lists /
 * `**bold**` / `*italic*` / `` `code` `` / `[text](http…)` links) that builds
 * React elements directly — no `dangerouslySetInnerHTML`, no markdown library.
 */
import { useState, type ReactNode } from 'react';

import {
  BarChart,
  DeltaBadge,
  DonutChart,
  Gauge,
  Heatmap,
  KpiTile,
  LineChart,
  Sparkline,
  compactNumber,
} from '@/routes/analytics/charts';
import { FunnelChart, Leaderboard, PieChart } from '@/routes/crm/charts-extra';
import { Card, CardGrid, CardThumb, DataGrid, InsightBadge, type CellValue, type DataGridColumn } from '@/ui';

import type {
  CalloutBlock,
  CardItem,
  CardsBlock,
  ChartBlock,
  ChartPoint,
  FormBlock,
  FormField,
  GridBlock,
  KpiBlock,
  KpiGroupBlock,
  MarkdownBlock,
  RichAction,
  TableBlock,
  TableColumn,
  TextBlock,
  Tone,
  Trend,
} from './blocks';

// ---------------------------------------------------------------------------
// text
// ---------------------------------------------------------------------------

export function TextBlockView({ block }: { block: TextBlock }): ReactNode {
  if (!block.text) return null;
  return <p className="whitespace-pre-wrap text-sm text-foreground">{block.text}</p>;
}

// ---------------------------------------------------------------------------
// markdown — tiny, safe, dependency-free renderer
// ---------------------------------------------------------------------------

interface MdBlockNode {
  kind: 'p' | 'h' | 'ul' | 'ol';
  level?: number;
  text?: string;
  items?: string[];
}

function parseMarkdownBlocks(src: string): MdBlockNode[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const nodes: MdBlockNode[] = [];
  let paraBuf: string[] = [];
  const flushPara = () => {
    if (paraBuf.length > 0) {
      nodes.push({ kind: 'p', text: paraBuf.join('\n') });
      paraBuf = [];
    }
  };
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      flushPara();
      nodes.push({ kind: 'h', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length) {
        const m = /^[-*]\s+(.*)$/.exec(lines[i]);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      nodes.push({ kind: 'ul', items });
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      flushPara();
      const items: string[] = [];
      while (i < lines.length) {
        const m = /^\d+\.\s+(.*)$/.exec(lines[i]);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      nodes.push({ kind: 'ol', items });
      continue;
    }
    if (line.trim() === '') {
      flushPara();
      i += 1;
      continue;
    }
    paraBuf.push(line);
    i += 1;
  }
  flushPara();
  return nodes;
}

/** Inline `**bold**` / `*italic*` / `_italic_` / `` `code` `` / `[text](http…)`. */
function renderInline(text: string): ReactNode[] {
  const pattern =
    /`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_/g;
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIndex) out.push(text.slice(lastIndex, m.index));
    if (m[1] !== undefined) {
      out.push(
        <code key={key++} className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[0.85em]">
          {m[1]}
        </code>,
      );
    } else if (m[2] !== undefined) {
      const href = m[3];
      const safeHref = /^https?:\/\//i.test(href) ? href : undefined;
      out.push(
        safeHref ? (
          <a
            key={key++}
            href={safeHref}
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline underline-offset-2 hover:opacity-80"
          >
            {m[2]}
          </a>
        ) : (
          m[2]
        ),
      );
    } else if (m[4] !== undefined || m[5] !== undefined) {
      out.push(
        <strong key={key++} className="font-semibold">
          {m[4] ?? m[5]}
        </strong>,
      );
    } else if (m[6] !== undefined || m[7] !== undefined) {
      out.push(<em key={key++}>{m[6] ?? m[7]}</em>);
    }
    lastIndex = pattern.lastIndex;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

function renderParagraph(text: string): ReactNode {
  const lines = text.split('\n');
  return lines.map((line, i) => (
    <span key={i}>
      {renderInline(line)}
      {i < lines.length - 1 && <br />}
    </span>
  ));
}

const HEADING_CLS: Record<number, string> = {
  1: 'text-lg font-bold',
  2: 'text-base font-bold',
  3: 'text-base font-semibold',
  4: 'text-sm font-semibold',
  5: 'text-sm font-semibold',
  6: 'text-sm font-semibold',
};

export function MarkdownBlockView({ block }: { block: MarkdownBlock }): ReactNode {
  const nodes = parseMarkdownBlocks(block.markdown || '');
  if (nodes.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 text-sm text-foreground">
      {nodes.map((n, i) => {
        if (n.kind === 'h') {
          return (
            <div key={i} className={HEADING_CLS[n.level ?? 3] ?? HEADING_CLS[3]}>
              {renderInline(n.text ?? '')}
            </div>
          );
        }
        if (n.kind === 'ul') {
          return (
            <ul key={i} className="ml-5 list-disc space-y-1">
              {(n.items ?? []).map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ul>
          );
        }
        if (n.kind === 'ol') {
          return (
            <ol key={i} className="ml-5 list-decimal space-y-1">
              {(n.items ?? []).map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={i} className="whitespace-pre-wrap">
            {renderParagraph(n.text ?? '')}
          </p>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// kpi / kpi_group — reuses analytics/charts KpiTile + DeltaBadge
// ---------------------------------------------------------------------------

function formatKpiValue(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return compactNumber(value);
  if (value == null || value === '') return '—';
  return String(value);
}

const TREND_STYLE: Record<Trend, { arrow: string; cls: string }> = {
  up: { arrow: '▲', cls: 'text-success' },
  down: { arrow: '▼', cls: 'text-danger' },
  flat: { arrow: '■', cls: 'text-muted' },
};

export function KpiBlockView({ block }: { block: KpiBlock }): ReactNode {
  const hasDelta = typeof block.delta === 'number' && Number.isFinite(block.delta);
  const trend = block.trend ?? undefined;
  const deltaNode = hasDelta ? (
    <DeltaBadge value={block.delta as number} />
  ) : trend ? (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${TREND_STYLE[trend].cls}`}>
      <span aria-hidden="true">{TREND_STYLE[trend].arrow}</span>
    </span>
  ) : undefined;

  const valueNode = (
    <>
      {formatKpiValue(block.value)}
      {block.unit && <span className="ml-1 text-base font-medium text-muted">{block.unit}</span>}
    </>
  );

  return (
    <KpiTile label={block.label || ''} value={valueNode} delta={deltaNode} hint={block.hint ?? undefined} />
  );
}

export function KpiGroupBlockView({ block }: { block: KpiGroupBlock }): ReactNode {
  if (!block.items || block.items.length === 0) return null;
  return (
    <CardGrid>
      {block.items.map((item, i) => (
        <KpiBlockView key={i} block={{ ...item, type: 'kpi' }} />
      ))}
    </CardGrid>
  );
}

// ---------------------------------------------------------------------------
// table — reuses @/ui DataGrid
// ---------------------------------------------------------------------------

type TableRow = Record<string, unknown> & { __rk: string };

function humanizeKey(key: string): string {
  const cleaned = key.replace(/_/g, ' ').replace(/-/g, ' ').trim();
  if (!cleaned) return key;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function inferColumns(rows: Record<string, unknown>[]): TableColumn[] {
  const seen: string[] = [];
  for (const row of rows.slice(0, 20)) {
    for (const key of Object.keys(row)) {
      if (!seen.includes(key)) seen.push(key);
    }
  }
  return seen.map((key) => ({ key, label: humanizeKey(key) }));
}

function cellSortValue(value: unknown): CellValue {
  if (value == null) return null;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value;
  return String(value);
}

function formatCell(value: unknown, type?: string | null): ReactNode {
  if (value == null || value === '') return <span className="text-muted">—</span>;
  if (type === 'number' && typeof value === 'number') {
    return new Intl.NumberFormat('fr-FR').format(value);
  }
  if (type === 'date') {
    const d = new Date(value as string | number);
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('fr-FR');
  }
  if (type === 'badge') {
    return <InsightBadge>{String(value)}</InsightBadge>;
  }
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function TableBlockView({ block }: { block: TableBlock }): ReactNode {
  const rows = block.rows ?? [];
  const columns = block.columns && block.columns.length > 0 ? block.columns : inferColumns(rows);
  if (columns.length === 0) return null;

  const keyedRows: TableRow[] = rows.map((r, i) => ({ ...r, __rk: String(i) }));
  const gridColumns: DataGridColumn<TableRow>[] = columns.map((c) => ({
    key: c.key,
    header: c.label || humanizeKey(c.key),
    sortable: true,
    filter: c.type === 'number' ? 'number' : c.type === 'date' ? 'date' : c.type === 'badge' ? undefined : 'text',
    value: (row) => cellSortValue(row[c.key]),
    render: (row) => formatCell(row[c.key], c.type),
  }));

  return (
    <div className="flex flex-col gap-2">
      {block.title && <h3 className="text-sm font-semibold text-foreground">{block.title}</h3>}
      <DataGrid columns={gridColumns} rows={keyedRows} rowKey={(r) => r.__rk} emptyLabel="Aucune donnée." />
      {block.pagination && block.pagination.total > rows.length && (
        <p className="text-xs text-muted">
          Page {block.pagination.page} · {rows.length} sur {block.pagination.total} résultats
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// cards / grid — reuses @/ui Card / CardGrid / CardThumb / InsightBadge
// ---------------------------------------------------------------------------

function CardItemView({ item }: { item: CardItem }): ReactNode {
  const thumb = item.thumbnail;
  const isUrl = !!thumb && /^https?:\/\//i.test(thumb);
  return (
    <Card className="h-full">
      {thumb && (
        <CardThumb height="h-28" className="mb-3">
          {isUrl ? (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img src={thumb} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-3xl leading-none">{thumb}</span>
          )}
        </CardThumb>
      )}
      <div className="flex flex-1 flex-col gap-2">
        <div>
          <div className="font-semibold text-foreground">{item.title || ''}</div>
          {item.subtitle && <div className="text-xs text-muted">{item.subtitle}</div>}
        </div>
        {item.fields && item.fields.length > 0 && (
          <dl className="space-y-1">
            {item.fields.map((f, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-xs">
                <dt className="text-muted">{f.label}</dt>
                <dd className="min-w-0 truncate font-medium text-foreground">
                  {f.value == null || f.value === '' ? '—' : String(f.value)}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {item.badges && item.badges.length > 0 && (
          <div className="mt-auto flex flex-wrap gap-1 pt-1">
            {item.badges.map((b, i) => (
              <InsightBadge key={i}>{b}</InsightBadge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function CardsBlockView({ block }: { block: CardsBlock }): ReactNode {
  if (!block.items || block.items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {block.title && <h3 className="text-sm font-semibold text-foreground">{block.title}</h3>}
      <CardGrid>
        {block.items.map((item, i) => (
          <CardItemView key={i} item={item} />
        ))}
      </CardGrid>
    </div>
  );
}

const GRID_COLS_CLASS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  6: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

export function GridBlockView({ block }: { block: GridBlock }): ReactNode {
  if (!block.items || block.items.length === 0) return null;
  const cols = block.columns != null ? Math.min(6, Math.max(1, Math.round(block.columns))) : null;
  return (
    <div className="flex flex-col gap-2">
      {block.title && <h3 className="text-sm font-semibold text-foreground">{block.title}</h3>}
      {cols != null ? (
        <div className={`grid auto-rows-fr gap-4 ${GRID_COLS_CLASS[cols]}`}>
          {block.items.map((item, i) => (
            <CardItemView key={i} item={item} />
          ))}
        </div>
      ) : (
        <CardGrid>
          {block.items.map((item, i) => (
            <CardItemView key={i} item={item} />
          ))}
        </CardGrid>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// chart — dispatches chart_type onto the existing SVG chart kit
// ---------------------------------------------------------------------------

function firstSeriesPoints(block: ChartBlock): ChartPoint[] {
  const series = block.series ?? [];
  if (series.length > 0) return series[0].points ?? [];
  return block.data ?? [];
}

function toFunnelRows(rows: Record<string, unknown>[]) {
  return rows.map((r) => {
    const label = r.label != null ? String(r.label) : '';
    const countRaw = r.count ?? r.value ?? 0;
    const count = typeof countRaw === 'number' ? countRaw : Number(countRaw) || 0;
    const value = r.value != null && !Number.isNaN(Number(r.value)) ? Number(r.value) : undefined;
    const conversion =
      r.conversion != null && !Number.isNaN(Number(r.conversion)) ? Number(r.conversion) : undefined;
    const dropoff = r.dropoff != null && !Number.isNaN(Number(r.dropoff)) ? Number(r.dropoff) : undefined;
    return { label, count, value, conversion, dropoff };
  });
}

function toLeaderboardRows(rows: Record<string, unknown>[]) {
  return rows.map((r) => {
    const label = r.label != null ? String(r.label) : '';
    const valueRaw = r.value ?? r.count ?? 0;
    const value = typeof valueRaw === 'number' ? valueRaw : Number(valueRaw) || 0;
    const rank = r.rank != null && !Number.isNaN(Number(r.rank)) ? Number(r.rank) : undefined;
    return { label, value, rank };
  });
}

function normalizeHeatmapRows(rows: Record<string, unknown>[]): {
  matrix: number[][];
  rowLabels: string[];
  colLabels: string[];
} {
  const rowOrder: string[] = [];
  const colOrder: string[] = [];
  const values = new Map<string, number>();
  for (const r of rows) {
    const rowRaw = r.row ?? r.weekday ?? r.y ?? r.label;
    const colRaw = r.col ?? r.hour ?? r.x ?? r.bucket;
    const valRaw = r.value ?? 0;
    const val = typeof valRaw === 'number' ? valRaw : Number(valRaw) || 0;
    const rk = rowRaw == null ? '' : String(rowRaw);
    const ck = colRaw == null ? '' : String(colRaw);
    if (!rowOrder.includes(rk)) rowOrder.push(rk);
    if (!colOrder.includes(ck)) colOrder.push(ck);
    values.set(`${rk} ${ck}`, val);
  }
  const matrix = rowOrder.map((rk) => colOrder.map((ck) => values.get(`${rk} ${ck}`) ?? 0));
  return { matrix, rowLabels: rowOrder, colLabels: colOrder };
}

function ChartBody({ block }: { block: ChartBlock }): ReactNode {
  const data = block.data ?? [];
  switch (block.chart_type) {
    case 'pie':
      return <PieChart data={data} />;
    case 'donut': {
      const total = data.reduce((s, d) => s + Math.max(0, d.value), 0);
      return (
        <DonutChart
          data={data}
          centerValue={total > 0 ? compactNumber(total) : undefined}
          centerLabel={total > 0 ? 'total' : undefined}
        />
      );
    }
    case 'bar':
    case 'histogram':
      return <BarChart data={data} />;
    case 'line':
    case 'area': {
      const series = block.series ?? [];
      if (series.length > 1) {
        return (
          <div className="flex flex-col gap-4">
            {series.map((s, i) => (
              <div key={i}>
                {s.name && <div className="mb-1 text-xs font-medium text-muted">{s.name}</div>}
                <LineChart points={s.points ?? []} />
              </div>
            ))}
          </div>
        );
      }
      return <LineChart points={firstSeriesPoints(block)} />;
    }
    case 'gauge': {
      const value = block.value ?? 0;
      const max = block.max ?? 100;
      const valueLabel = block.unit ? `${compactNumber(value)} ${block.unit}` : undefined;
      return (
        <div className="flex justify-center py-2">
          <Gauge value={value} max={max} valueLabel={valueLabel} size={140} />
        </div>
      );
    }
    case 'funnel':
      return <FunnelChart rows={toFunnelRows(block.rows ?? [])} />;
    case 'leaderboard':
      return <Leaderboard rows={toLeaderboardRows(block.rows ?? [])} />;
    case 'heatmap': {
      const { matrix, rowLabels, colLabels } = normalizeHeatmapRows(block.rows ?? []);
      if (rowLabels.length === 0 || colLabels.length === 0) {
        return <p className="py-6 text-center text-sm text-muted">Aucune donnée.</p>;
      }
      return <Heatmap data={matrix} rowLabels={rowLabels} colLabels={colLabels} />;
    }
    case 'sparkline':
      return (
        <div className="py-1">
          <Sparkline values={block.values ?? []} width={260} height={56} />
        </div>
      );
    default:
      return null;
  }
}

export function ChartBlockView({ block }: { block: ChartBlock }): ReactNode {
  return (
    <div className="rounded-token-lg border border-border bg-surface p-4 shadow-elevation-1">
      {block.title && (
        <h3 className="mb-3 text-sm font-semibold text-foreground">
          {block.title}
          {block.unit && <span className="ml-1 text-xs font-normal text-muted">({block.unit})</span>}
        </h3>
      )}
      <ChartBody block={block} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// form — dynamic field dispatch (mirrors PublicFormPage / form-schema.ts)
// ---------------------------------------------------------------------------

const INPUT_CLS =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted focus:border-primary focus:outline-none';

function initialFormValues(fields: FormField[]): Record<string, string | boolean> {
  const values: Record<string, string | boolean> = {};
  for (const f of fields) {
    values[f.name] = f.type === 'checkbox' ? false : '';
  }
  return values;
}

export function FormBlockView({
  block,
  onAction,
}: {
  block: FormBlock;
  onAction?: (action: RichAction) => void;
}): ReactNode {
  const fields = block.fields ?? [];
  const [values, setValues] = useState<Record<string, string | boolean>>(() => initialFormValues(fields));
  const [sentOnce, setSentOnce] = useState(false);

  if (fields.length === 0) return null;

  const setField = (name: string, v: string | boolean) => {
    setValues((prev) => ({ ...prev, [name]: v }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onAction?.({
      type: 'submit_form',
      formId: block.submit?.formId ?? undefined,
      tool: block.submit?.tool ?? undefined,
      values,
    });
    setSentOnce(true);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-token-lg border border-border bg-surface p-4 shadow-elevation-1"
    >
      {block.title && <h3 className="text-sm font-semibold text-foreground">{block.title}</h3>}
      {fields.map((f) => {
        const type = f.type || 'text';
        const value = values[f.name];

        if (type === 'checkbox') {
          return (
            <label key={f.name} className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={Boolean(value)}
                required={f.required}
                onChange={(e) => setField(f.name, e.target.checked)}
                className="h-4 w-4 rounded border-border accent-primary focus:ring-ring"
              />
              {f.label || f.name}
              {f.required && <span className="text-danger"> *</span>}
            </label>
          );
        }

        const common = {
          id: `rr-${f.name}`,
          required: Boolean(f.required),
          value: typeof value === 'string' ? value : '',
          className: INPUT_CLS,
        };

        return (
          <label key={f.name} className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-foreground">
              {f.label || f.name}
              {f.required && <span className="text-danger"> *</span>}
            </span>
            {type === 'textarea' ? (
              <textarea
                {...common}
                rows={4}
                placeholder={f.placeholder ?? undefined}
                onChange={(e) => setField(f.name, e.target.value)}
              />
            ) : type === 'select' && Array.isArray(f.options) && f.options.length > 0 ? (
              <select {...common} onChange={(e) => setField(f.name, e.target.value)}>
                <option value="">—</option>
                {f.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                {...common}
                placeholder={f.placeholder ?? undefined}
                type={['email', 'tel', 'number', 'url', 'date'].includes(type) ? type : 'text'}
                onChange={(e) => setField(f.name, e.target.value)}
              />
            )}
          </label>
        );
      })}
      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-token bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-elevation-1 transition hover:brightness-110"
        >
          {block.submitLabel || 'Envoyer'}
        </button>
        {sentOnce && <span className="text-xs text-success">Envoyé ✓</span>}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// divider / callout
// ---------------------------------------------------------------------------

export function DividerBlockView(): ReactNode {
  return <hr className="border-border" />;
}

const CALLOUT_STYLE: Record<Tone, string> = {
  info: 'border-primary/30 bg-primary/10 text-primary',
  success: 'border-success/30 bg-success/10 text-success',
  warning: 'border-warning/30 bg-warning/10 text-warning',
  danger: 'border-danger/30 bg-danger/10 text-danger',
  neutral: 'border-border bg-surface-2 text-foreground',
};

export function CalloutBlockView({ block }: { block: CalloutBlock }): ReactNode {
  const cls = CALLOUT_STYLE[block.tone] ?? CALLOUT_STYLE.info;
  const isAlert = block.tone === 'danger' || block.tone === 'warning';
  return (
    <div role={isAlert ? 'alert' : 'status'} className={`rounded-token-lg border px-4 py-3 text-sm ${cls}`}>
      {block.title && <div className="mb-1 font-semibold">{block.title}</div>}
      <div className="whitespace-pre-wrap">{block.text}</div>
    </div>
  );
}
